import { InvitationInfoData } from "../models/invitationsApiTypes"

export type GeneratedInvitationMessage = {
  invitationId: string
  invitationName: string
  pin: string
  message: string
}

export type PlaceholderDescriptor = {
  token: string
  description: string
  example: string
}

const RSVP_CONFIRM_PATH = "/confirm"
const WEDDING_QUERY_PARAM = "wedding"
const CODE_QUERY_PARAM = "code"

function normalizeUrl(url: string): string {
  return url.trim().replace(/\/+$/, "")
}

function hasValue(value: string | undefined | null): value is string {
  return value != null && value.trim().length > 0
}

function appendCodeQueryParam(url: string, pin: string): string {
  if (!url) {
    return ""
  }

  const separator = url.includes("?")
    ? url.endsWith("?") || url.endsWith("&")
      ? ""
      : "&"
    : "?"

  return `${url}${separator}${CODE_QUERY_PARAM}=${encodeURIComponent(pin)}`
}

function buildRsvpBaseUrl(tenant: string): string {
  const baseRsvpUrl = normalizeUrl(process.env.REACT_APP_RSVP_URL ?? "")
  const encodedTenant = encodeURIComponent(tenant)

  return `${baseRsvpUrl}${RSVP_CONFIRM_PATH}?${WEDDING_QUERY_PARAM}=${encodedTenant}`
}

export function buildRsvpUrlWithPin(tenant: string, pin: string): string {
  return appendCodeQueryParam(buildRsvpBaseUrl(tenant), pin)
}

export function buildWebsiteUrlWithPin(frontendUrl: string | undefined | null, pin: string): string {
  if (!hasValue(frontendUrl)) {
    return ""
  }

  return appendCodeQueryParam(frontendUrl.trim(), pin)
}

export function resolveInvitationMessage(
  template: string,
  invitation: InvitationInfoData,
  tenant: string,
  frontendUrl?: string | null,
): string {
  const invitationName = invitation.name
  const pin = invitation.reservationCode
  const rsvpUrl = buildRsvpBaseUrl(tenant)
  const websiteUrl = hasValue(frontendUrl) ? frontendUrl.trim() : ""

  const replacements: Record<string, string> = {
    "{invitationName}": invitationName,
    "{pin}": pin,
    "{rsvpUrl}": rsvpUrl,
    "{rsvpUrlWithPin}": buildRsvpUrlWithPin(tenant, pin),
    "{websiteUrl}": websiteUrl,
    "{websiteUrlWithPin}": buildWebsiteUrlWithPin(frontendUrl, pin),
  }

  return Object.entries(replacements).reduce((resolvedTemplate, [token, replacement]) => {
    return resolvedTemplate.split(token).join(replacement)
  }, template)
}

export function getAvailablePlaceholders(
  frontendUrl?: string | null,
  previewInvitation?: InvitationInfoData | null,
  tenant?: string,
): PlaceholderDescriptor[] {
  const hasWebsitePlaceholders = hasValue(frontendUrl)
  const previewRecord = previewInvitation ?? null
  const exampleInvitationName = previewRecord?.name ?? "Famiglia Rossi"
  const examplePin = previewRecord?.reservationCode ?? "ABC123"
  const exampleTenant = hasValue(tenant) ? tenant.trim() : "demo-tenant"
  const websiteUrlExample = hasWebsitePlaceholders ? frontendUrl.trim() : ""
  const websiteUrlWithPinExample = hasWebsitePlaceholders
    ? buildWebsiteUrlWithPin(frontendUrl, examplePin)
    : ""

  const placeholders: PlaceholderDescriptor[] = [
    {
      token: "{invitationName}",
      description: "Nome dell'invito",
      example: exampleInvitationName,
    },
    {
      token: "{pin}",
      description: "Codice PIN univoco dell'invito",
      example: examplePin,
    },
    {
      token: "{rsvpUrl}",
      description: "Link RSVP base con tenant",
      example: buildRsvpBaseUrl(exampleTenant),
    },
    {
      token: "{rsvpUrlWithPin}",
      description: "Link RSVP con tenant e PIN",
      example: buildRsvpUrlWithPin(exampleTenant, examplePin),
    },
  ]

  if (hasWebsitePlaceholders) {
    placeholders.push(
      {
        token: "{websiteUrl}",
        description: "Link al sito del matrimonio",
        example: websiteUrlExample,
      },
      {
        token: "{websiteUrlWithPin}",
        description: "Link al sito del matrimonio con PIN",
        example: websiteUrlWithPinExample,
      },
    )
  }

  return placeholders
}

export function buildPreviewRecord(invitations: InvitationInfoData[]): InvitationInfoData | null {
  if (invitations.length === 0) {
    return null
  }

  return invitations[0]
}
